import { type MetaFunction } from '@remix-run/node'
import { json, Link, useLoaderData } from '@remix-run/react'
import Masonry from 'react-masonry-css'
import { CloudFrontImage } from '#app/components/cloudfront-image.js'
import Main from '#app/components/main.js'
import Sidebar from '#app/components/sidebar.js'
import { Icon } from '#app/components/ui/icon.js'
import { Separator } from '#app/components/ui/separator.js'
import { useTheme } from '#app/root.js'
import { prisma } from '#app/utils/db.server.js'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [{ title: `Kogia | Contributors` }]
}

export const loader = async () => {
	const artists = await prisma.user.findMany({
		select: {
			name: true,
			username: true,
			id: true,
			photo: {
				select: {
					width: true,
					height: true,
					work: {
						select: {
							id: true,
							originalAssetUrl: true,
						},
					},
				},
			},
		},
		where: {
			works: {
				some: {},
			},
			roles: {
				some: {
					name: {
						in: ['image', 'video'],
					},
				},
			},
		},
		orderBy: {
			name: 'asc',
		},
	})
	return json({ artists: artists })
}

export default function ArtistsIndex() {
	const theme = useTheme()
	const { artists } = useLoaderData<typeof loader>()
	return (
		<>
			<Sidebar>
				<div className="flex flex-col items-start gap-4 overflow-hidden p-2">
					<h2 className="font-serif text-4xl">Contributors</h2>
					<p>
						Our Kogia Oceanic Library would not be possible without the generous
						donations of photos and videos from our contributors.
					</p>
					<Separator className="flex-1" />
				</div>
				<div className="overflow-auto">
					{artists.map(artist => (
						<Link
							prefetch="intent"
							to={`/contributors/${artist.username}`}
							key={`artists-index-page-artist-sidebar-${artist.id}`}
							className="flex items-center justify-between rounded-sm bg-transparent p-2 hover:bg-accent focus:bg-accent"
						>
							<h3 className="leading-none">{artist.name}</h3>
							<Icon name="external-link" className="h-4 w-4" />
						</Link>
					))}
				</div>
			</Sidebar>
			<Main>
				<Masonry
					breakpointCols={{
						default: 3,
						1280: 2,
						768: 1,
					}}
					className="my-masonry-grid"
					columnClassName="my-masonry-grid_column"
				>
					{artists.map(artist => (
						<Link
							prefetch="intent"
							to={`/contributors/${artist.username}`}
							key={`artists-index-page-artist-main-${artist.id}`}
							className="group mb-8 block outline-offset-8 outline-primary"
						>
							<div className="relative overflow-hidden">
								{/* {artist.photo?.work ? (
									<AspectRatio
										ratio={
											artist.photo.width && artist.photo.height
												? artist.photo.width / artist.photo.height
												: 16 / 9
										}
										className="bg-accent"
									>
										<CloudFrontImage
											work={{
												id: artist.photo.work.id,
												originalAssetUrl: artist.photo.work.originalAssetUrl,
												name: `${artist.name} profile picture`,
												user: {
													name: artist.name,
												},
											}}
										/>
									</AspectRatio>
								) : (
									<div className="aspect-h-9 aspect-w-16 relative bg-muted">
										<div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2">
											<img
												src="/logos/kogia-logo-light-theme.png"
												alt="Kogia Logo"
												className="h-auto w-1/3"
											/>
											<p className="text-xs">Profile picture coming soon</p>
										</div>
									</div>
								)} */}
								{artist.photo?.work ? (
									<CloudFrontImage
										work={{
											id: artist.photo.work.id,
											originalAssetUrl: artist.photo.work.originalAssetUrl,
											name: `${artist.name} profile picture`,
											user: {
												name: artist.name,
											},
										}}
									/>
								) : (
									<div className="aspect-h-9 aspect-w-16 relative bg-muted">
										<div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2">
											{theme === 'dark' ? (
												<>
													<img
														src="/logos/kogia-logo-dark-theme-compressed.png"
														alt="Kogia Logo"
														className="h-auto w-1/3"
													/>
													<p className="text-sm text-white">
														Profile picture coming soon
													</p>
												</>
											) : (
												<>
													<img
														src="/logos/kogia-logo-light-theme-compressed.png"
														alt="Kogia Logo"
														className="h-auto w-1/3"
													/>
													<p className="text-sm">Profile picture coming soon</p>
												</>
											)}
										</div>
									</div>
								)}
							</div>
							<div className="mt-2 flex items-center gap-4 opacity-50 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
								<h3 className="font-serif text-xl font-bold">
									{artist.name ?? 'Unknown Contributor'}
								</h3>
								<Separator className="flex-1 bg-primary" />
							</div>
						</Link>
					))}
				</Masonry>
			</Main>
		</>
	)
}
